import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import {
    Handshake,
    Users,
    Presentation,
    BookOpen,
    Mail,
    Phone,
    Globe,
    MapPin,
    X
} from 'lucide-react'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'


const AVENUES = [
    {
        icon: Presentation,
        title: 'Seminars & Conferences',
        text: 'Co-host national and international seminars, symposia and conferences on contemporary political and governance themes.'
    },
    {
        icon: Users,
        title: 'Panel Discussions',
        text: 'Convene expert panels and roundtables that bring scholars, practitioners and policymakers into dialogue.'
    },
    {
        icon: BookOpen,
        title: 'Joint Research',
        text: 'Undertake collaborative research projects and special issues with research centres in India and abroad.'
    },
    {
        icon: Handshake,
        title: 'Academic Exchange',
        text: 'Exchange ideas, expertise and scholarship to enrich work on politics, foreign policy and public policy.'
    }
]

const CONTACT = [
    { icon: MapPin, label: 'Address', value: 'Room No. 220, Department of Political Science, Faculty of Social Science, University of Delhi – 110007, India' },
    { icon: Phone, label: 'Phone', value: '+91 95551 22120', href: 'tel:+919555122120' },
    { icon: Mail, label: 'Email', value: 'indianstudiesreview@gmail.com', href: 'mailto:indianstudiesreview@gmail.com' },
    { icon: Mail, label: 'Email', value: 'cspgindia@gmail.com', href: 'mailto:cspgindia@gmail.com' },
    { icon: Globe, label: 'Website', value: 'www.cspgindia.in', href: 'https://www.cspgindia.in' }
]

function InterestModal({ onClose }: { onClose: () => void }) {
    const [form, setForm] = useState({ name: '', institution: '', email: '', area: '', message: '' })

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
        window.addEventListener('keydown', onKey)
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            window.removeEventListener('keydown', onKey)
            document.body.style.overflow = prev
        }
    }, [onClose])

    const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setForm((f) => ({ ...f, [k]: e.target.value }))

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        const subject = encodeURIComponent(`Collaboration enquiry — ${form.institution || form.name || 'CSPG'}`)
        const body = encodeURIComponent(
            `Name: ${form.name}\nInstitution: ${form.institution}\nEmail: ${form.email}\nArea of collaboration: ${form.area}\n\n${form.message}`
        )
        window.location.href = `mailto:indianstudiesreview@gmail.com?subject=${subject}&body=${body}`
        onClose()
    }

    const field: React.CSSProperties = {
        border: '1px solid var(--color-rule-strong)',
        background: 'white',
        borderRadius: '2px',
        color: 'var(--color-ink-700)'
    }

    const node = (
        <div
            role="dialog"
            aria-modal
            aria-label="Express interest in collaborating"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'rgba(13,11,8,0.6)', backdropFilter: 'blur(4px)' }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <motion.div
                initial={{ opacity: 0, y: 18, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="w-full max-w-lg overflow-hidden"
                style={{ background: 'var(--color-paper-50)', border: '1px solid var(--color-gold-800)', borderRadius: '2px', maxHeight: '92vh' }}
            >
                <div
                    className="flex items-center justify-between px-6 py-4"
                    style={{ background: 'var(--color-gold-800)', color: 'var(--color-paper-50)' }}
                >
                    <div>
                        <div className="text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.2em', color: 'var(--color-gold-300)' }}>
                            Collaboration
                        </div>
                        <div className="font-display text-[1.15rem] mt-0.5">Express your interest</div>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 hover:opacity-70">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={submit} className="px-6 py-5 overflow-y-auto" style={{ maxHeight: 'calc(92vh - 4.5rem)' }}>
                    <p className="text-[13.5px] leading-relaxed mb-4" style={{ color: 'var(--color-ink-500)' }}>
                        Tell us a little about your institution and the kind of collaboration
                        you have in mind. Submitting opens your email client with the details
                        pre-filled.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-[11px] uppercase font-semibold" style={{ letterSpacing: '0.1em', color: 'var(--color-ink-400)' }}>Name</span>
                            <input required value={form.name} onChange={set('name')} className="w-full mt-1 px-3 py-2 text-[14px]" style={field} />
                        </label>
                        <label className="block">
                            <span className="text-[11px] uppercase font-semibold" style={{ letterSpacing: '0.1em', color: 'var(--color-ink-400)' }}>Institution</span>
                            <input value={form.institution} onChange={set('institution')} className="w-full mt-1 px-3 py-2 text-[14px]" style={field} />
                        </label>
                    </div>
                    <label className="block mt-3">
                        <span className="text-[11px] uppercase font-semibold" style={{ letterSpacing: '0.1em', color: 'var(--color-ink-400)' }}>Email</span>
                        <input required type="email" value={form.email} onChange={set('email')} className="w-full mt-1 px-3 py-2 text-[14px]" style={field} />
                    </label>
                    <label className="block mt-3">
                        <span className="text-[11px] uppercase font-semibold" style={{ letterSpacing: '0.1em', color: 'var(--color-ink-400)' }}>Area of collaboration</span>
                        <input value={form.area} onChange={set('area')} placeholder="e.g. joint seminar, special issue" className="w-full mt-1 px-3 py-2 text-[14px]" style={field} />
                    </label>
                    <label className="block mt-3">
                        <span className="text-[11px] uppercase font-semibold" style={{ letterSpacing: '0.1em', color: 'var(--color-ink-400)' }}>Message</span>
                        <textarea value={form.message} onChange={set('message')} rows={4} className="w-full mt-1 px-3 py-2 text-[14px]" style={field} />
                    </label>
                    <div className="mt-5 flex gap-3">
                        <button type="submit" className="btn btn-gold flex-1">Send enquiry</button>
                        <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
                    </div>
                </form>
            </motion.div>
        </div>
    )

    return createPortal(node, document.body)
}

export default function CallForAcademicCollaborations() {
    const [modalOpen, setModalOpen] = useState(false)

    return (
        <>
            <SEO
                title="Call for Academic Collaborations"
                description="Indian Studies Review and the Centre for Study of Politics and Governance invite institutions and scholars to collaborate on seminars, conferences, joint research and academic events."
                path="/call-for-academic-collaborations"
            />
            <PageHeader
                eyebrow="About the Centre"
                title="Call for Academic Collaborations"
                lede="Indian Studies Review invites institutions and scholars to collaborate on seminars, conferences and academic events."
            />

            <section className="section">
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55 }}
                        className="grid lg:grid-cols-[1fr_auto] gap-8 items-center pb-10"
                        style={{ borderBottom: '1px solid var(--color-rule-strong)' }}
                    >
                        <div className="prose-body" style={{ maxWidth: '44rem' }}>
                            <p>
                                Indian Studies Review, published by the Centre for Study of
                                Politics and Governance, Delhi, invites academic institutions,
                                research centres and scholarly organisations to collaborate with
                                us in organising seminars, conferences, panel discussions and
                                other academic events.
                            </p>
                            <p>
                                We welcome partnerships that foster rigorous scholarship,
                                interdisciplinary dialogue and critical engagement on themes
                                related to Indian politics, governance, society, history and
                                culture.
                            </p>
                        </div>
                        <button type="button" onClick={() => setModalOpen(true)} className="btn btn-gold whitespace-nowrap">
                            Express interest →
                        </button>
                    </motion.div>

                    <div className="mt-12">
                        <div className="kicker">Ways to collaborate</div>
                        <div className="mt-6 grid sm:grid-cols-2 gap-px" style={{ background: 'var(--color-rule)', border: '1px solid var(--color-rule)' }}>
                            {AVENUES.map((a, i) => {
                                const Icon = a.icon
                                return (
                                    <motion.div
                                        key={a.title}
                                        initial={{ opacity: 0, y: 12 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.45, delay: (i % 2) * 0.06 }}
                                        className="p-7"
                                        style={{ background: 'var(--color-paper-50)' }}
                                    >
                                        <Icon size={24} style={{ color: 'var(--color-gold-700)' }} />
                                        <h3 className="font-display mt-4" style={{ fontSize: '1.2rem', color: 'var(--color-ink-700)', letterSpacing: '-0.01em' }}>
                                            {a.title}
                                        </h3>
                                        <p className="mt-2.5 text-[14.5px] leading-relaxed" style={{ color: 'var(--color-ink-500)' }}>
                                            {a.text}
                                        </p>
                                    </motion.div>
                                )
                            })}
                        </div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55 }}
                        className="mt-12 p-7 md:p-9"
                        style={{ background: 'var(--color-paper-100)', borderLeft: '3px solid var(--color-gold-500)', borderRadius: '2px' }}
                    >
                        <div className="kicker" style={{ color: 'var(--color-accent-500)' }}>Institutional Collaboration</div>
                        <p className="prose-body mt-4" style={{ maxWidth: '46rem' }}>
                            CSPG intends to undertake research projects with other esteemed
                            research institutes in the country and abroad. The aim of the
                            collaboration is to facilitate the exchange of ideas and knowledge,
                            and to work together in building and enriching expertise on
                            contemporary politics, foreign policies and public policies.
                        </p>
                        <p className="prose-body mt-3" style={{ maxWidth: '46rem' }}>
                            CSPG is currently in talks with a number of institutions for
                            producing joint work and substantive collaborative research.
                        </p>
                    </motion.div>

                    <div className="mt-14">
                        <div className="kicker kicker--center">Contact Details</div>
                        <div className="mt-7 grid md:grid-cols-2 gap-5">
                            <div
                                className="p-6"
                                style={{ border: '1px solid var(--color-rule-strong)', background: 'var(--color-paper-50)' }}
                            >
                                <div className="font-display" style={{ fontSize: '1.15rem', color: 'var(--color-ink-700)' }}>
                                    Centre for Study of Politics &amp; Governance
                                </div>
                                <ul className="mt-4 flex flex-col gap-3.5">
                                    {CONTACT.map((c) => {
                                        const Icon = c.icon
                                        return (
                                            <li key={c.label + c.value} className="flex gap-3 items-start">
                                                <Icon size={16} style={{ color: 'var(--color-gold-700)', flexShrink: 0, marginTop: 3 }} />
                                                <div>
                                                    <div className="text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.14em', color: 'var(--color-ink-400)' }}>
                                                        {c.label}
                                                    </div>
                                                    {c.href ? (
                                                        <a
                                                            href={c.href}
                                                            target={c.href.startsWith('http') ? '_blank' : undefined}
                                                            rel={c.href.startsWith('http') ? 'noreferrer' : undefined}
                                                            className="text-[14px] hover:text-[var(--color-gold-700)] transition-colors"
                                                            style={{ color: 'var(--color-ink-700)' }}
                                                        >
                                                            {c.value}
                                                        </a>
                                                    ) : (
                                                        <div className="text-[14px] leading-relaxed" style={{ color: 'var(--color-ink-700)' }}>
                                                            {c.value}
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>

                            <div
                                className="p-7 flex flex-col justify-center text-center"
                                style={{ background: 'var(--color-gold-800)', color: 'var(--color-paper-50)' }}
                            >
                                <Handshake size={30} className="mx-auto" style={{ color: 'var(--color-gold-300)' }} />
                                <h3 className="font-display mt-4" style={{ fontSize: '1.4rem', color: 'var(--color-paper-50)', letterSpacing: '-0.02em' }}>
                                    Let’s build something together
                                </h3>
                                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: 'rgba(243,236,217,0.72)' }}>
                                    Interested institutions and scholars are encouraged to connect
                                    with us to explore collaborative opportunities.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(true)}
                                    className="btn btn-gold mt-6 mx-auto"
                                >
                                    Express interest →
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {modalOpen && <InterestModal onClose={() => setModalOpen(false)} />}
        </>
    )
}
